import { RadioGroup as NextUIRadioGroup, Radio } from '@nextui-org/react'
import React, { ChangeEventHandler, useEffect, useState } from 'react'
import { useCompactViewport } from '~/hooks/useCompactViewport'

interface Option {
  label: React.ReactNode
  value: string
}

interface RadioGroupProps {
  options: Option[]
  onChange: ChangeEventHandler<HTMLInputElement>
  defaultValue?: string
  className?: string
  id?: string
  isDisabled?: boolean
  label?: string
}

const RadioGroup: React.FC<RadioGroupProps> = ({
  options,
  onChange,
  defaultValue,
  className,
  id,
  isDisabled = false,
  label
}) => {
  const isPlugin = useCompactViewport()
  const [selectedValue, setSelectedValue] = useState<string>(defaultValue || '')

  useEffect(() => {
    if (defaultValue) {
      setSelectedValue(defaultValue)
    }
  }, [defaultValue])

  const handleChange = (value: string) => {
    if (isDisabled) return
    setSelectedValue(value)
    // Create a synthetic event to maintain compatibility with the original onChange handler
    const syntheticEvent = {
      target: {
        value,
        checked: true,
        type: 'radio',
        name: id
      }
    } as React.ChangeEvent<HTMLInputElement>

    onChange(syntheticEvent)
  }

  return (
    <NextUIRadioGroup
      value={selectedValue}
      onValueChange={handleChange}
      orientation='horizontal'
      isDisabled={isDisabled}
      label={label}
      className={className}
      classNames={{
        wrapper: isPlugin ? 'gap-x-3 gap-y-2 flex-wrap' : 'gap-x-[48px]',
        label: 'text-[12px] select-none'
      }}
    >
      {options.map((option, index) => (
        <Radio
          key={option.value}
          value={option.value}
          className={`${id}-item-${index + 1} select-none`}
          data-testid={`${id}-item-${option.value}`}
        >
          {option.label}
        </Radio>
      ))}
    </NextUIRadioGroup>
  )
}

export default RadioGroup
